import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import BusyOverlay from "common/components/BusyOverlay";
import { TripB2BError } from "trips/models";
import { useSelector } from "react-redux";
import { vehicleTripData } from "store/vehicleTripSlice";
import {
  DataGrid,
  GridColDef,
  GridCellModes,
  GridCellModesModel,
  GridCellParams,
  useGridApiRef,
  GridToolbarContainer,
} from "@mui/x-data-grid";
import moment from "moment";
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  GlobalStyles,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { AddBox, Error as ErrorIcon, Warning as WarningIcon, ExpandMore, ExpandLess, RemoveCircle, DragIndicator, FileDownload as ExportIcon, Visibility, VisibilityOff, Map as MapIcon, GpsFixed as GpsFixedIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import { GetTimeZoneFromAbbreviation } from "common/Constants";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import { IMaskInput } from "react-imask";
import { applyGpsOdometers, prepareGpsPings, prepareGpsSegments } from "./gpsOdometer";
import { resolveDateCellValue, resolveTimeCellValue, shouldWarnDiscardedEntry } from "./eventTime";
import { seedTimesBetween, SPACING_WARNING } from "./eventSpacing";
import CloserTooltip from "./CloserTooltip";
import { useMaterialUIController } from "context";
import { readableIconColor, readableMuted, readableText } from "assets/theme/boldText";
import {
  applySuggestedLocations,
  hasApplicableSuggestion,
  isUnselectableSuggestion,
  sumEventMiles,
} from "./suggestedLocations";


// Date input component
const DateEditCell = ({ params, timeZone }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const tz = GetTimeZoneFromAbbreviation(timeZone);
  // If time is null, start with empty placeholder format
  const initialValue = params.row.time
    ? moment.utc(params.row.time).tz(tz).format("MM/DD/YY")
    : "";
  const [value, setValue] = useState(initialValue);
  // Latest masked text, readable from the unmount cleanup without a stale closure.
  const latestValue = useRef(initialValue);
  // Warning is fired from an unmount cleanup that can race the blur timeout, and
  // Escape is a deliberate cancel that must stay silent.
  const warnedRef = useRef(false);
  const cancelledRef = useRef(false);

  // An incomplete entry commits nothing, which leaves the original value on screen
  // and looks exactly like a successful edit. Say so, otherwise the closer walks away
  // thinking the date changed. The cell reverts on its own because `time` was never
  // touched.
  //
  // This hangs off unmount rather than blur because BLUR NEVER FIRES ON ENTER: the
  // grid tears the input out of the DOM to commit the row, and React does not
  // dispatch blur for a removed element. Reproduced on trip 6733128 - Tab warned,
  // Enter silently reverted. Unmount is the one path every exit shares (Enter, Tab,
  // clicking another cell, clicking a toolbar button).
  const warnIfDiscarded = () => {
    if (warnedRef.current || cancelledRef.current) return;
    const masked = latestValue.current;
    if (shouldWarnDiscardedEntry(masked, initialValue, resolveDateCellValue(masked, params.row.time, timeZone))) {
      warnedRef.current = true;
      toast.warning("Date not changed — enter a complete date as MM/DD/YY.");
    }
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
    return () => warnIfDiscarded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (newValue: string) => {
    setValue(newValue);
    latestValue.current = newValue;
    // Only commit once the mask holds a real, complete date.
    //
    // The previous guard tested part LENGTH, which the mask's own placeholders
    // satisfy: with lazy={false}, typing "07" over a selected cell leaves
    // "07/__/__", whose three parts are all length 2. parseInt("__") is NaN, and
    // feeding that to moment produced a VALID but wrong date, which then committed
    // silently — a closer typing a partial date got a changed year or day and no
    // warning. resolveDateCellValue requires actual digits and verifies the result
    // is the date that was asked for, returning null for anything incomplete.
    const resolved = resolveDateCellValue(newValue, params.row.time, timeZone);
    if (resolved) {
      // Set the edit cell value so it's in internal state before blur
      params.api.setEditCellValue({ id: params.id, field: 'date', value: resolved });
    }
  };

  return (
    <IMaskInput
      mask="00/00/00"
      lazy={false}
      autofix={true}
      inputRef={inputRef}
      value={value}
      onAccept={handleChange}
      onFocus={(e) => {
        e.target.select();
      }}
      onKeyDown={(e) => {
        // Escape discards on purpose - the closer knows the edit is gone.
        if (e.key === "Escape") cancelledRef.current = true;
      }}
      onBlur={() => {
        warnIfDiscarded();
        // Small delay to ensure onAccept has fired
        setTimeout(() => {
          try {
            params.api.stopCellEditMode({ id: params.id, field: 'date', ignoreModifications: false });
          } catch (e) {
            // Cell may already be out of edit mode
          }
        }, 100);
      }}
      style={{
        width: '100%',
        height: '100%',
        border: 'none',
        outline: 'none',
        padding: '8px',
        fontSize: 'inherit',
        fontFamily: 'inherit'
      }}
    />
  );
};

// Time input component
const TimeEditCell = ({ params, timeZone, tripEvents, setTripEvents }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const tz = GetTimeZoneFromAbbreviation(timeZone);
  // If time is null, start with empty placeholder format
  const initialValue = params.row.time
    ? moment.utc(params.row.time).tz(tz).format("HH:mm:ss")
    : "";
  const [value, setValue] = useState(initialValue);
  // Latest masked text, readable from the unmount cleanup without a stale closure.
  const latestValue = useRef(initialValue);
  const warnedRef = useRef(false);
  const cancelledRef = useRef(false);

  // Same reasoning as the Date cell: an incomplete entry commits nothing, the old
  // value stays on screen, and that is indistinguishable from a saved edit. Fired
  // from unmount because blur never fires when Enter commits the row - see the
  // comment on the Date cell's warnIfDiscarded.
  const warnIfDiscarded = () => {
    if (warnedRef.current || cancelledRef.current) return;
    const masked = latestValue.current;
    if (shouldWarnDiscardedEntry(masked, initialValue, resolveTimeCellValue(masked, params.row.time, timeZone))) {
      warnedRef.current = true;
      toast.warning("Time not changed — enter at least HH:MM (seconds optional).");
    }
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
    return () => warnIfDiscarded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (newValue: string) => {
    setValue(newValue);
    latestValue.current = newValue;
    // Digits only, not just length: the mask's "__" placeholders pass a length
    // check, and parseInt("__") is NaN, which moment turns into a wrong-but-valid
    // time that commits silently. Same defect the Date cell had.
    //
    // Seconds are optional and default to 00 — must match resolveTimeCellValue, which
    // re-checks this on commit. Typing four digits leaves the mask at "14:52:__", so
    // that form has to commit as 14:52:00 or the edit is silently thrown away on tab-out.
    const timeParts = /^(\d{2}):(\d{2})(?::(\d{2}))?[^\d]*$/.exec(newValue);
    const [hours, minutes, typedSeconds] = timeParts ? timeParts.slice(1) : [];
    const seconds = typedSeconds === undefined ? "0" : typedSeconds;
    if (timeParts && parseInt(hours) <= 23 && parseInt(minutes) <= 59 && parseInt(seconds) <= 59) {
      // If no existing time, default to today in the trip's timezone
      const baseTime = params.row.time
        ? moment.utc(params.row.time).tz(tz)
        : moment.tz(tz);
      const newDateTime = baseTime.clone()
        .hours(parseInt(hours))
        .minutes(parseInt(minutes))
        .seconds(parseInt(seconds));

      // Handle day rollover: if time change crosses midnight, adjust the date
      if (params.row.time) {
        const oldHour = baseTime.hours();
        const newHour = parseInt(hours);
        // Crossed midnight forward (e.g., 23:xx -> 00:xx or 01:xx)
        if (oldHour >= 18 && newHour < 6) {
          newDateTime.add(1, 'day');
        }
        // Crossed midnight backward (e.g., 00:xx or 01:xx -> 23:xx)
        else if (oldHour < 6 && newHour >= 18) {
          newDateTime.subtract(1, 'day');
        }
      }

      const newTimeValue = newDateTime.utc().toISOString();
      // Set the edit cell value so it's in internal state before blur
      params.api.setEditCellValue({ id: params.id, field: 'timeOnly', value: newTimeValue });
    }
  };

  const handleBlur = () => {
    warnIfDiscarded();
    // Small delay to ensure onAccept has fired
    setTimeout(() => {
      try {
        params.api.stopCellEditMode({ id: params.id, field: 'timeOnly', ignoreModifications: false });
      } catch (e) {
        // Cell may already be out of edit mode
      }
    }, 100);
  };

  return (
    <IMaskInput
      mask="00:00:00"
      lazy={false}
      autofix={true}
      overwrite={true}
      inputRef={inputRef}
      value={value}
      onAccept={handleChange}
      onFocus={(e) => {
        e.target.select();
      }}
      onKeyDown={(e) => {
        // Escape discards on purpose - the closer knows the edit is gone.
        if (e.key === "Escape") cancelledRef.current = true;
      }}
      onBlur={handleBlur}
      style={{
        width: '100%',
        height: '100%',
        border: 'none',
        outline: 'none',
        padding: '8px',
        fontSize: 'inherit',
        fontFamily: 'inherit'
      }}
    />
  );
};

// Custom select edit cell that doesn't auto-open dropdown
const SelectEditCell = ({ params, options, field }) => {
  const inputRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <select
      ref={inputRef}
      value={params.value || ''}
      onChange={(e) => {
        params.api.setEditCellValue({ id: params.id, field, value: e.target.value });
      }}
      onKeyDown={(e) => {
        // Prevent native Tab so the grid's key handler can manage cell navigation
        if (e.key === 'Tab' || e.key === 'Enter') {
          e.preventDefault();
        }
      }}
      onBlur={() => {
        // Commit the edit when focus leaves the select
        setTimeout(() => {
          try {
            params.api.stopCellEditMode({ id: params.id, field, ignoreModifications: false });
          } catch (ex) {
            // Cell may already be out of edit mode
          }
        }, 50);
      }}
      style={{
        width: '100%',
        height: '100%',
        border: 'none',
        outline: 'none',
        padding: '0 8px',
        fontSize: 'inherit',
        fontFamily: 'inherit',
        background: 'transparent',
        cursor: 'pointer',
      }}
    >
      <option value=""></option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );
};

// Odometer edit cell with proper focus on tab
const OdometerEditCell = ({ params }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [displayValue, setDisplayValue] = useState(params.value ? String(Math.trunc(params.value)) : '');

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    // Only handle digit keys for overwrite
    if (e.key.length === 1 && /[0-9]/.test(e.key) && input.selectionStart != null) {
      const pos = input.selectionStart;
      const selEnd = input.selectionEnd ?? pos;
      // If no selection range, overwrite the character at cursor
      if (pos === selEnd && pos < displayValue.length) {
        e.preventDefault();
        const newVal = displayValue.substring(0, pos) + e.key + displayValue.substring(pos + 1);
        setDisplayValue(newVal);
        const numValue = newVal ? parseInt(newVal, 10) : null;
        params.api.setEditCellValue({ id: params.id, field: params.field, value: numValue });
        // Restore cursor position after React re-render
        requestAnimationFrame(() => {
          input.setSelectionRange(pos + 1, pos + 1);
        });
      }
    }
  };

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      value={displayValue}
      onChange={(e) => {
        const raw = e.target.value.replace(/[^0-9]/g, '');
        setDisplayValue(raw);
        const newValue = raw ? parseInt(raw, 10) : null;
        params.api.setEditCellValue({ id: params.id, field: params.field, value: newValue });
      }}
      onKeyDown={handleKeyDown}
      style={{
        width: '100%',
        height: '100%',
        border: 'none',
        outline: 'none',
        padding: '8px',
        fontSize: 'inherit',
        fontFamily: 'inherit',
        textAlign: 'right',
      }}
    />
  );
};

// Labeled value used by the mobile card view. Phone users mostly review trips
// rather than edit them, so on small screens we render each event as a readable
// card instead of forcing a sideways scroll through the dense editable grid.
const CardField = ({ label, value }: { label: string; value: any }) => {
  const [{ boldText }] = useMaterialUIController();
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, color: readableMuted(boldText, "text.secondary"), display: "block", lineHeight: 1.2 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
        {value === null || value === undefined || value === "" ? "—" : value}
      </Typography>
    </Box>
  );
};

// Event type ordering: trip flow order first, then alphabetical for others
const EVENT_TYPE_ORDER = [
  "Begin Trip",
  "Arrived at Pickup",
  "Depart with Crew",
  "Wait Start",
  "Wait End",
  "Arrived at Destination",
  "Drop off Crew",
  "End Trip",
];

interface CloserEventsGridProps {
  tripId?: number;
  isClosed?: boolean;
  readOnly?: boolean;
  tripEvents: any[];
  setTripEvents: (events: any[]) => void;
  eventTypes: any[];
  waitReasons: any[];
  pointsOfService: any[];
  timeZone: string;
  validationErrors: any[];
  validationWarnings: any[];
  showValidationErrors: boolean;
  setShowValidationErrors: (show: boolean) => void;
  expandedValidationErrors: boolean;
  setExpandedValidationErrors: (expanded: boolean) => void;
  isLoading: boolean;
  approveLoading: boolean;
  /** What the approve buttons say while they work -- approving saves first, then finalizes. */
  approveStatus?: string | null;
  /** Names the operation covering the grid, e.g. "Saving events". Absent means nothing is running. */
  busyMessage?: string | null;
  /** Second line for operations that take a while, e.g. the GPS rebuild. */
  busyDetail?: string | null;
  onApprove: () => void;
  onSaveAndValidate: () => void;
  gridCollapsed: boolean;
  setGridCollapsed: (collapsed: boolean) => void;
  activeFilter: string;
  setActiveFilter: (filter: string) => void;
  canOverride?: boolean;
  overrideReason?: string;
  onOverrideFinalize?: () => void;
  onDeactivateAll?: () => void;
  validationLoaded?: boolean;
  /** The railroad's outstanding rejection, shown as its own banner on the closing screen. */
  b2bError?: TripB2BError | null;
  hasAllowApprove?: boolean;
  hasAllowOverride?: boolean;
  hasAllowClearAllEvents?: boolean;
  isApproved?: boolean;
  onReopen?: () => void;
  onExport?: () => void;
  exportLoading?: boolean;
  onGoToMap?: (eventId: number) => void;
  mapDataReady?: boolean;
  isVerified?: boolean;
  onVerify?: (verified: boolean) => void;
  verifyLoading?: boolean;
  isMapped?: boolean;
  onSetManagerReview?: (mapped: boolean) => void;
  managerReviewLoading?: boolean;
  onToggleEventActive?: (event: any, isActive: boolean) => void;
  acceptedWaitCount?: number;
  acceptedWaitMinutes?: number;
  dismissedWaitCount?: number;
  dismissedWaitMinutes?: number;
  gpsFirstMove?: string | null;
  gpsLastMove?: string | null;
  allowApplyGpsOdometers?: boolean;
  // Admin-only, and only when the trip has GPS: without GPS a reset would delete the events and
  // rebuild nothing.
  allowEventReset?: boolean;
  onEventReset?: () => void;
  eventResetLoading?: boolean;
}

const CloserEventsGrid: React.FC<CloserEventsGridProps> = ({
  tripId,
  isClosed,
  readOnly,
  gpsFirstMove,
  gpsLastMove,
  tripEvents,
  setTripEvents,
  eventTypes,
  waitReasons,
  pointsOfService,
  timeZone,
  validationErrors,
  validationWarnings,
  showValidationErrors,
  setShowValidationErrors,
  expandedValidationErrors,
  setExpandedValidationErrors,
  isLoading,
  approveLoading,
  approveStatus,
  busyMessage,
  busyDetail,
  onApprove,
  onSaveAndValidate,
  gridCollapsed,
  setGridCollapsed,
  activeFilter,
  setActiveFilter,
  canOverride,
  overrideReason,
  onOverrideFinalize,
  onDeactivateAll,
  validationLoaded,
  b2bError,
  hasAllowApprove,
  hasAllowOverride,
  hasAllowClearAllEvents,
  isApproved,
  onReopen,
  onExport,
  exportLoading,
  onGoToMap,
  mapDataReady,
  isVerified,
  onVerify,
  verifyLoading,
  isMapped,
  onSetManagerReview,
  managerReviewLoading,
  onToggleEventActive,
  acceptedWaitCount = 0,
  acceptedWaitMinutes = 0,
  dismissedWaitCount = 0,
  dismissedWaitMinutes = 0,
  allowApplyGpsOdometers = false,
  allowEventReset = false,
  onEventReset,
  eventResetLoading = false,
}) => {
  const [{ boldText }] = useMaterialUIController();
  const _vehicleActivity = useSelector(vehicleTripData);
  const requriedEventNames = [
    "Begin Trip",
    "End Trip",
    "Arrived at Pickup",
    "Depart with Crew",
    "Arrived at Destination",
    "Drop off Crew",
  ];

  // Below md the editable spreadsheet is unreadable on a phone, so we swap it for
  // a stacked read-only card list. Desktop (md+) keeps the full DataGrid.
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Calculate wait statistics from current events
  const waitStats = useMemo(() => {
    const waitStartTypeId = eventTypes?.find((et) => et.name === "Wait Start")?.id;
    const waitEndTypeId = eventTypes?.find((et) => et.name === "Wait End")?.id;

    if (!waitStartTypeId || !tripEvents) {
      return { existingCount: 0, existingMinutes: 0, suggestedCount: 0, suggestedMinutes: 0 };
    }

    // Existing waits: saved events (have eventId > 0 and not suggested)
    const existingWaitStarts = tripEvents.filter(
      (e) => e.typeId === waitStartTypeId && e.eventId && e.eventId > 0 && !e.isSuggested
    );

    // Suggested waits: events marked as suggested
    const suggestedWaitStarts = tripEvents.filter(
      (e) => e.typeId === waitStartTypeId && e.isSuggested
    );

    // Calculate minutes from suggestion context or by pairing with Wait End
    const calculateMinutes = (waitStarts: any[], isSuggested: boolean) => {
      let totalMinutes = 0;
      for (const start of waitStarts) {
        if (isSuggested && start.suggestionContext?.originalDurationMinutes) {
          totalMinutes += start.suggestionContext.originalDurationMinutes;
        } else {
          // Find matching Wait End by looking for next Wait End after this start
          const startTime = new Date(start.time).getTime();
          const matchingEnd = tripEvents.find(
            (e) =>
              e.typeId === waitEndTypeId &&
              new Date(e.time).getTime() > startTime &&
              e.isSuggested === start.isSuggested
          );
          if (matchingEnd) {
            const endTime = new Date(matchingEnd.time).getTime();
            totalMinutes += (endTime - startTime) / 60000;
          }
        }
      }
      return totalMinutes;
    };

    return {
      existingCount: existingWaitStarts.length,
      existingMinutes: calculateMinutes(existingWaitStarts, false),
      suggestedCount: suggestedWaitStarts.length,
      suggestedMinutes: calculateMinutes(suggestedWaitStarts, true),
    };
  }, [tripEvents, eventTypes]);

  // Drag and drop state
  const [draggedRow, setDraggedRow] = useState<any>(null);
  const [dropTargetRow, setDropTargetRow] = useState<any>(null);
  const [dropPosition, setDropPosition] = useState<'top' | 'bottom'>('bottom');

  const handleDragStart = (event: React.DragEvent, row: any) => {
    setDraggedRow(row);
    event.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (event: React.DragEvent, row: any) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";

    // Determine if we should insert above or below based on mouse position
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const midpoint = rect.top + rect.height / 2;
    const position = event.clientY < midpoint ? 'top' : 'bottom';

    setDropTargetRow(row);
    setDropPosition(position);
  };

  const handleDrop = (event: React.DragEvent, targetRow: any) => {
    event.preventDefault();

    if (!draggedRow || draggedRow.id === targetRow.id) {
      setDraggedRow(null);
      setDropTargetRow(null);
      return;
    }

    const draggedIndex = tripEvents.findIndex((e) => e.id === draggedRow.id);
    const targetIndex = tripEvents.findIndex((e) => e.id === targetRow.id);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedRow(null);
      setDropTargetRow(null);
      return;
    }

    // Check if dragged event is suggested
    const isSuggestedEvent = draggedRow.isSuggested ||
      (draggedRow.closingComments && draggedRow.closingComments.includes('SUGGESTED'));

    // Check if this would result in no change (dropping to same effective position)
    // Case 1: Dropping below the item immediately above (B below A when B is already after A)
    // Case 2: Dropping above the item immediately below (B above C when B is already before C)
    // For suggested events, we still want to recalculate time based on neighbors
    if (!isSuggestedEvent &&
        ((dropPosition === 'bottom' && targetIndex === draggedIndex - 1) ||
         (dropPosition === 'top' && targetIndex === draggedIndex + 1))) {
      setDraggedRow(null);
      setDropTargetRow(null);
      return;
    }

    const newEvents = [...tripEvents];
    const [originalRemoved] = newEvents.splice(draggedIndex, 1);
    // Create a mutable copy of the removed event
    let removed = { ...originalRemoved };

    // Calculate insert index after removal
    // When we remove an item, all indices after it shift down by 1
    let insertIndex;
    if (dropPosition === 'bottom') {
      // Insert after target
      insertIndex = draggedIndex < targetIndex ? targetIndex : targetIndex + 1;
    } else {
      // Insert before target
      insertIndex = draggedIndex < targetIndex ? targetIndex - 1 : targetIndex;
    }

    // Bounds check for safety
    insertIndex = Math.max(0, Math.min(insertIndex, newEvents.length));

    newEvents.splice(insertIndex, 0, removed);

    // Set the dragged event's time to 1 minute before the next event (or after the previous
    // one), kept strictly between its new neighbours so it never ties with either.
    const eventBelow = insertIndex < newEvents.length - 1 ? newEvents[insertIndex + 1] : null;
    const eventAbove = insertIndex > 0 ? newEvents[insertIndex - 1] : null;

    const { times, fits } = seedTimesBetween(eventAbove?.time, eventBelow?.time, 1, 60000);
    const newTime = times[0] ?? removed.time;

    if (newTime !== removed.time) {
      newEvents[insertIndex] = {
        ...newEvents[insertIndex],
        time: newTime,
        spacingWarning: fits ? undefined : { time: newTime, message: SPACING_WARNING },
      };
    }

    setTripEvents(newEvents);
    setDraggedRow(null);
    setDropTargetRow(null);
  };

  const handleDragEnd = () => {
    setDraggedRow(null);
    setDropTargetRow(null);
  };

  const deleteEvent = (id) => {
    const updatedEvents = tripEvents.filter((event) => event.id !== id);
    setTripEvents(updatedEvents);
  };

  // Re-derive every active event's odometer from the vehicle's GPS activity.
  //
  // The driver's app carries the previous event's odometer forward rather than
  // reading one per event, so a closer who fixes an event's time is still left
  // with the driver's stale mileage. This lets them make their time/order edits
  // first and then pull the whole odometer column back onto the GPS track in one
  // click, before Save and Validate. Matching maths lives in ./gpsOdometer.
  const handleApplyGpsOdometers = () => {
    const segments = prepareGpsSegments(_vehicleActivity?.activity);
    const pings = prepareGpsPings(_vehicleActivity?.telemetry);
    if (segments.length === 0 && pings.length === 0) {
      toast.warning("No GPS activity is available for this trip — odometers were not changed.");
      return;
    }

    const { events, updatedCount, unmatchedCount, interpolatedCount } = applyGpsOdometers(
      tripEvents,
      segments,
      pings,
      eventTypes
    );
    setTripEvents(events);

    if (updatedCount === 0) {
      toast.info("Odometers already match GPS — nothing changed.");
    } else {
      // Call out interpolated events: those fell in a gap in the ping stream and
      // are an estimate rather than a reading, so they're the ones worth a look.
      const caveats = [
        unmatchedCount > 0 ? `${unmatchedCount} had no usable time` : null,
        interpolatedCount > 0 ? `${interpolatedCount} estimated between GPS points` : null,
      ].filter(Boolean);
      toast.info(
        `Set the odometer on ${updatedCount} event(s) from GPS${caveats.length ? `; ${caveats.join(", ")}` : ""}. Review, then Save and Validate.`
      );
    }
  };

  const addEvent = (id) => {
    const addAboveEvent = tripEvents.find((event) => event.id === id);
    const newEvent = {
      id: Date.now(), // Generate a unique ID for the new event
      typeId: "",
      comments: "",
      time: addAboveEvent.time,
      routeLocation: addAboveEvent.routeLocation,
      suggestedLocation: "",
      odometer: null,
      lytxOdometer: "",
      closingComments: "",
      eventDriveTimeMiles: 0,
      driveTimeMiles: 0,
      eventDriveTimeMinutes: 0,
      driveTimeMinutes: 0,
      eventId: null,
      isActive: true,
    };

    const index = tripEvents.findIndex((event) => event.id === id);
    if (index !== -1) {
      // One minute before the clicked row, a second apart, and never on the same time as
      // each other or either neighbour.
      const { times, fits } = seedTimesBetween(tripEvents[index - 1]?.time, addAboveEvent.time, 2, 60000);
      const seed = (time: string | null, idOffset: number) => ({
        ...newEvent,
        id: Date.now() + idOffset,
        time: time ?? newEvent.time,
        spacingWarning: fits || !time ? undefined : { time, message: SPACING_WARNING },
      });
      const firstNewEvent = seed(times[0], 1);
      const secondNewEvent = seed(times[1], 2);
      const updatedEventsWithInsert = [
        ...tripEvents.slice(0, index),
        firstNewEvent,
        secondNewEvent,
        ...tripEvents.slice(index),
      ];
      setTripEvents(updatedEventsWithInsert);
    }
  };

  // Grid-level add: appends exactly ONE blank row AFTER the last event.
  //
  // The per-row "+" above is a wait-pair tool and cannot do this job. It inserts TWO
  // rows one minute BEFORE the row you clicked, and it only renders on rows that
  // already have an eventId — so a trip with no saved events has no row, therefore no
  // "+", therefore no way to add anything at all. Measured in QA over 90 days: 115
  // closed trips blocked on "End Trip missing" and 7 on "Begin Trip missing", none of
  // them overridable (TripService.CanOverrideValidationErrorsAsync reports
  // CanOverride: false for both), and Event Reset refuses to run until both anchors
  // exist. Hand-adding the anchor is the only way to unstick them, and this button is
  // the only route to it when the grid is empty.
  const addEventAtEnd = () => {
    const last = tripEvents.length ? tripEvents[tripEvents.length - 1] : null;

    // AFTER the last event, not before it: an End Trip has to sort last or
    // CheckEndTripOutOfSequence rejects it, so seeding one minute earlier would make
    // the closer hand-correct the time on every single add.
    //
    // With an empty grid there is nothing to count from, so fall back to the vehicle's
    // first GPS movement when we have it. It is the measured start of the trip, it is
    // already surfaced in this grid as the "GPS:" hint under a Begin Trip row, and it
    // spares the closer typing a date from scratch — while still being visibly wrong if
    // it is wrong, because both cells show it.
    //
    // Falling back to null is safe and deliberate: the Date and Time cells already
    // render "Enter date" / "Enter time" in grey italic for a row with no time. Be aware
    // of the entry order that fallback forces, though — resolveTimeCellValue refuses a
    // bare time when there is no date to anchor it to, so such a row needs its Date
    // filled in before its Time.
    const time = last?.time
      ? moment.utc(last.time).add(1, "minute").toISOString()
      : gpsFirstMove || null;

    const newEvent = {
      id: Date.now(),
      typeId: "",
      comments: "",
      time,
      routeLocation: last?.routeLocation ?? "",
      suggestedLocation: "",
      odometer: null,
      lytxOdometer: "",
      closingComments: "",
      eventDriveTimeMiles: 0,
      driveTimeMiles: 0,
      eventDriveTimeMinutes: 0,
      driveTimeMinutes: 0,
      eventId: null,
      isActive: true,
    };

    // The button lives in the toolbar, which stays on screen while the grid is hidden.
    // Adding a row into a collapsed grid reads as "nothing happened" and invites a
    // second click, so open the grid on the way.
    if (gridCollapsed) setGridCollapsed(false);
    setTripEvents([...tripEvents, newEvent]);
  };

  const mapEventTypesForRow = (row) => {
    const options = eventTypes?.filter((et) => et.name !== "Depart Destination").map((et) => ({ value: et.id, label: et.name })) || [];
    return options.sort((a, b) => {
      const aIndex = EVENT_TYPE_ORDER.indexOf(a.label);
      const bIndex = EVENT_TYPE_ORDER.indexOf(b.label);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.label.localeCompare(b.label);
    });
  };

  // Find the paired event for auto-populate.
  // Wait Start searches forward for Wait End, Wait End searches backward for Wait Start.
  // Skips over non-wait events (e.g. Depart with Crew between a pair).
  // Stops and returns null if another wait event of the same type is found first
  // (indicates nested pairs that shouldn't cross-sync).
  const endEventTypes = ["Wait End", "Depart with Crew", "Drop off Crew"];
  const waitStartTypeId = eventTypes.find((et) => et.name === "Wait Start")?.id;
  const waitEndTypeId = eventTypes.find((et) => et.name === "Wait End")?.id;
  const findPairedEvent = (eventId: any, pairedTypeName: string, changedTypeName: string, events: any[] = tripEvents) => {
    const pairedTypeId = eventTypes.find((et) => et.name === pairedTypeName)?.id;
    if (!pairedTypeId) return null;
    const changedIndex = events.findIndex((e) => e.id === eventId);
    if (changedIndex === -1) return null;
    const changedTypeId = events[changedIndex].typeId;
    const isWait = changedTypeId === waitStartTypeId || changedTypeId === waitEndTypeId;

    if (endEventTypes.includes(changedTypeName)) {
      for (let i = changedIndex - 1; i >= 0; i--) {
        if (events[i].typeId === pairedTypeId) return events[i];
        // Also match adjacent blank rows (newly added, typeId may not be set yet in this render)
        if (isWait && !events[i].typeId && !events[i].eventId) return events[i];
        if (isWait && (events[i].typeId === changedTypeId)) return null;
      }
    } else {
      for (let i = changedIndex + 1; i < events.length; i++) {
        if (events[i].typeId === pairedTypeId) return events[i];
        if (isWait && !events[i].typeId && !events[i].eventId) return events[i];
        if (isWait && (events[i].typeId === changedTypeId)) return null;
      }
    }
    return null;
  };

  // Pair maps for auto-populate
  const routeLocationPairMap = {
    "Arrived at Pickup": "Depart with Crew",
    "Depart with Crew": "Arrived at Pickup",
    "Arrived at Destination": "Drop off Crew",
    "Drop off Crew": "Arrived at Destination",
    "Wait Start": "Wait End",
    "Wait End": "Wait Start",
  };

  const waitReasonPairMap = {
    "Wait Start": "Wait End",
    "Wait End": "Wait Start",
  };

  // Sets an event's Route Location and carries it to the paired event, as picking from the
  // Route Location dropdown does. Shared by the cell edit and Apply Suggested Locations.
  const setRouteLocation = (events: any[], eventId: any, value: any, typeIdHint?: any) => {
    const changedTypeId = Number(typeIdHint) || events.find((e) => e.id === eventId)?.typeId;
    const changedTypeName = eventTypes.find((et) => et.id === changedTypeId)?.name;
    const pairedTypeName = routeLocationPairMap[changedTypeName || ""];
    const pairedEvent = pairedTypeName ? findPairedEvent(eventId, pairedTypeName, changedTypeName || "", events) : null;

    return events.map((event) => {
      if (event.id === eventId) return { ...event, routeLocation: value };
      if (pairedEvent && event.id === pairedEvent.id && event.comments?.toLowerCase() !== "assist") return { ...event, routeLocation: value };
      return event;
    });
  };

  const isEndTrip = (event: any) => (eventTypes.find((et) => et.id === event.typeId)?.name ?? event.type) === "End Trip";

  // Reindex events for sequential Entry numbers — server already filters by isActive
  const displayedEvents = useMemo(() => {
    return tripEvents.map((e, i) => ({ ...e, index: i + 1 }));
  }, [tripEvents]);

  const milesTotals = useMemo(() => sumEventMiles(displayedEvents), [displayedEvents]);

  // Same test the Route Location cell uses to show a name: the id must be a dropdown option.
  const isSelectableLocation = (id: number) => pointsOfService.some((l) => l.id == id);

  const applicableSuggestionCount = displayedEvents.filter((e) =>
    hasApplicableSuggestion(e, isEndTrip, isSelectableLocation)
  ).length;

  // Bulk version of picking each row's Suggested Location in its Route Location dropdown.
  // Only changes the unsaved grid; the closer still reviews and saves.
  const handleApplySuggestedLocations = () => {
    // displayedEvents is tripEvents row for row, so the grid's own state is the input.
    const skippedCount = tripEvents.filter((e) => isUnselectableSuggestion(e, isEndTrip, isSelectableLocation)).length;
    const skippedNote = skippedCount
      ? ` ${skippedCount} left as is: suggested location is not in this trip's location list.`
      : "";
    const { events, appliedCount } = applySuggestedLocations(
      tripEvents,
      isEndTrip,
      setRouteLocation,
      isSelectableLocation
    );
    if (appliedCount === 0) {
      toast.info(`No suggested locations to apply.${skippedNote}`);
      return;
    }
    setTripEvents(events);
    toast.info(
      `Applied ${appliedCount} suggested location${appliedCount === 1 ? "" : "s"}.${skippedNote} Review, then Save and Validate.`
    );
  };

  const columns: GridColDef[] = [
    {
      field: "drag",
      headerName: "",
      width: 32,
      minWidth: 32,
      sortable: false,
      renderCell: (params) => readOnly ? null : (
        <div
          draggable
          onDragStart={(e) => handleDragStart(e, params.row)}
          onDragOver={(e) => handleDragOver(e, params.row)}
          onDrop={(e) => handleDrop(e, params.row)}
          onDragEnd={handleDragEnd}
          style={{
            cursor: "grab",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            height: "100%",
            width: "100%",
            opacity: draggedRow?.id === params.row.id ? 0.5 : 1,
            paddingRight: 0,
          }}
        >
          <DragIndicator sx={{ color: "action.active", fontSize: "20px" }} />
        </div>
      ),
    },
    {
      field: "add",
      headerName: "",
      width: 25,
      sortable: false,
      renderCell: (params) => {
        if (readOnly) return null;
        // Show minus icon for new unsaved rows, plus icon for saved rows
        if (!params.row.eventId) {
          return (
            <IconButton title="Remove" onClick={() => deleteEvent(params.id)} sx={{ ml: 0 }}>
              <RemoveCircle></RemoveCircle>
            </IconButton>
          );
        }
        return (
          <IconButton title="Add" onClick={() => addEvent(params.id)} sx={{ ml: 0 }}>
            <AddBox></AddBox>
          </IconButton>
        );
      },
    },
    {
      field: "goToMap",
      headerName: "",
      width: 25,
      sortable: false,
      renderCell: (params) => {
        // Only show for events with an eventId (including suggested events with negative IDs) and when map data is ready
        if (!params.row.eventId || !mapDataReady) {
          return null;
        }
        return (
          <CloserTooltip title="Show in map" placement="top">
            <IconButton
              onClick={() => onGoToMap?.(params.row.eventId)}
              sx={{ ml: 0, color: readableIconColor(boldText), ...(boldText && { fontWeight: 600 }) }}
              size="small"
            >
              <MapIcon fontSize="small" />
            </IconButton>
          </CloserTooltip>
        );
      },
    },
    {
      field: "isActive",
      headerName: "",
      width: 32,
      minWidth: 32,
      sortable: false,
      renderCell: (params) => (
        <Checkbox
          checked={params.value ?? true}
          disabled={readOnly}
          onChange={(e) => {
            const event = tripEvents.find((ev) => ev.id === params.id);
            if (event && onToggleEventActive) {
              onToggleEventActive(event, e.target.checked);
            } else {
              const updatedEvents = tripEvents.map((ev) =>
                ev.id === params.id ? { ...ev, isActive: e.target.checked } : ev
              );
              setTripEvents(updatedEvents);
            }
          }}
          sx={{ padding: 0 }}
        />
      ),
    },

    {
      field: "index",
      headerName: "Entry",
      flex: 0.3,
      minWidth: 55,
      renderCell: (params) => (
        <CloserTooltip title={`Event ID: ${params.row.eventId || 'N/A'}`} placement="top">
          <span>{params.value}</span>
        </CloserTooltip>
      ),
    },
    {
      field: "typeId",
      headerName: "Event",
      flex: 1.2,
      minWidth: 120,
      editable: !readOnly,
      renderCell: (params) => {
        const eventType = eventTypes.find((et) => et.id == params.value);
        return <span>{eventType?.name || ''}</span>;
      },
      renderEditCell: (params) => (
        <SelectEditCell params={params} options={mapEventTypesForRow(params.row)} field="typeId" />
      ),
      preProcessEditCellProps: (params) => {
        const numericValue = params.props.value !== "" && params.props.value != null
          ? Number(params.props.value)
          : params.props.value;

        // Auto-populate paired event type for wait pairs
        const changedTypeName = eventTypes.find((et) => et.id === numericValue)?.name;
        const waitPairMap: Record<string, string> = { "Wait Start": "Wait End", "Wait End": "Wait Start" };
        const pairedTypeName = waitPairMap[changedTypeName || ""];
        const pairedTypeId = pairedTypeName ? eventTypes.find((et) => et.name === pairedTypeName)?.id : null;

        const changedIndex = tripEvents.findIndex((e) => e.id === params.id);
        let pairedIndex = -1;
        if (pairedTypeId != null && changedIndex !== -1) {
          // Look for adjacent blank row (no typeId) to auto-fill
          if (changedTypeName === "Wait Start") {
            // Search forward for blank or Wait End row
            for (let i = changedIndex + 1; i < tripEvents.length; i++) {
              if (!tripEvents[i].typeId) { pairedIndex = i; break; }
              if (tripEvents[i].typeId === pairedTypeId) { pairedIndex = i; break; }
              break; // stop at first non-blank, non-paired row
            }
          } else if (changedTypeName === "Wait End") {
            // Search backward for blank or Wait Start row
            for (let i = changedIndex - 1; i >= 0; i--) {
              if (!tripEvents[i].typeId) { pairedIndex = i; break; }
              if (tripEvents[i].typeId === pairedTypeId) { pairedIndex = i; break; }
              break;
            }
          }
        }

        // Changing a wait event to a non-wait type drops its wait reason, so the event is
        // not saved carrying a reason that no longer applies to it.
        const isWaitTypeId = (typeId: any) => {
          const id = Number(typeId);
          return id === waitStartTypeId || id === waitEndTypeId;
        };
        const clearsWaitReason =
          changedIndex !== -1 && isWaitTypeId(tripEvents[changedIndex].typeId) && !isWaitTypeId(numericValue);

        const updatedEvents = tripEvents.map((event, i) => {
          if (event.id === params.id) return clearsWaitReason ? { ...event, typeId: numericValue, comments: "" } : { ...event, typeId: numericValue };
          if (i === pairedIndex && pairedTypeId != null && event.comments?.toLowerCase() !== "assist") return { ...event, typeId: pairedTypeId };
          return event;
        });
        setTripEvents(updatedEvents);
        return { ...params.props, value: numericValue };
      },
    },
    {
      field: "comments",
      headerName: "Wait/Cancel Reason",
      flex: 1.5,
      minWidth: 100,
      editable: !readOnly,
      renderEditCell: (params) => (
        <SelectEditCell
          params={params}
          options={[{ value: "", label: "\u00A0" }, ...(waitReasons?.map((m) => ({ value: m.name, label: m.name })) || [])]}
          field="comments"
        />
      ),
      preProcessEditCellProps: (params) => {
        const changedTypeId = Number(params.row?.typeId) || tripEvents.find((e) => e.id === params.id)?.typeId;
        const changedTypeName = eventTypes.find((et) => et.id === changedTypeId)?.name;
        const pairedTypeName = waitReasonPairMap[changedTypeName || ""];
        const pairedEvent = pairedTypeName ? findPairedEvent(params.id, pairedTypeName, changedTypeName || "") : null;

        const updatedEvents = tripEvents.map((event) => {
          if (event.id === params.id) return { ...event, comments: params.props.value };
          if (pairedEvent && event.id === pairedEvent.id) return { ...event, comments: params.props.value };
          return event;
        });
        setTripEvents(updatedEvents);
        return { ...params.props };
      },
    },
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      minWidth: 100,
      editable: !readOnly,
      valueGetter: (params) => {
        if (!params.row.time) return "";
        return moment.utc(params.row.time).tz(GetTimeZoneFromAbbreviation(timeZone)).format("MM/DD/YY");
      },
      renderCell: (params) => (
        <span style={!params.row.time ? { color: readableMuted(boldText, '#9e9e9e'), fontStyle: 'italic' } : undefined}>
          {params.row.time
            ? moment.utc(params.row.time).tz(GetTimeZoneFromAbbreviation(timeZone)).format("MM/DD/YY")
            : "Enter date"}
        </span>
      ),
      renderEditCell: (params) => <DateEditCell params={params} timeZone={timeZone} />,
      preProcessEditCellProps: (params) => {
        // The committed value is an ISO timestamp on the happy path, but can be the
        // valueGetter's display string ("MM/DD/YY") when the cell is left without a
        // complete entry. Writing that straight into `time` only fails later, on save:
        // it parses as midnight UTC and the UTC->local conversion drops the event to
        // the PREVIOUS day. Resolve it properly, and leave `time` alone if we can't.
        const existing = tripEvents.find((event) => event.id === params.id)?.time;
        const resolved = resolveDateCellValue(params.props.value, existing, timeZone);
        if (resolved !== null) {
          const updatedEvents = tripEvents.map((event) =>
            event.id === params.id ? { ...event, time: resolved } : event
          );
          setTripEvents(updatedEvents);
        }
        return { ...params.props };
      },
    },
    {
      field: "timeOnly",
      headerName: "Time",
      flex: 1,
      minWidth: 100,
      editable: !readOnly,
      valueGetter: (params) => {
        if (!params.row.time) return "";
        return moment.utc(params.row.time).tz(GetTimeZoneFromAbbreviation(timeZone)).format("HH:mm:ss");
      },
      renderCell: (params) => {
        // For Begin Trip / End Trip: show GPS-derived actual move time underneath the
        // driver-recorded time. Computed in CloserLayout from the VehicleActivity segments
        // already loaded for the map — no extra backend call. Lets dispatchers spot a
        // late button press inline without leaving Flow for the legacy Dashboard.
        const eventType = params.row.type;
        const gpsActual =
          eventType === "Begin Trip" ? gpsFirstMove :
          eventType === "End Trip" ? gpsLastMove : null;
        // Set by add / drag-drop when the row could not be given a time a full second from
        // its neighbours. Shown only until the closer changes the time.
        const spacingWarning =
          params.row.spacingWarning && params.row.spacingWarning.time === params.row.time
            ? params.row.spacingWarning.message
            : null;
        return (
          <span style={!params.row.time ? { color: readableMuted(boldText, '#9e9e9e'), fontStyle: 'italic' } : undefined}>
            {spacingWarning && (
              <CloserTooltip title={spacingWarning}>
                <WarningIcon color="warning" sx={{ fontSize: 16, verticalAlign: "middle", mr: 0.5 }} />
              </CloserTooltip>
            )}
            {params.row.time
              ? `${moment.utc(params.row.time).tz(GetTimeZoneFromAbbreviation(timeZone)).format("HH:mm:ss")} ${timeZone}`
              : "Enter time"}
            {gpsActual && (
              <span style={{ display: "block", fontSize: "max(11px, 0.85em)", color: readableMuted(boldText, "#666") }}>
                GPS: {moment.utc(gpsActual).tz(GetTimeZoneFromAbbreviation(timeZone)).format("HH:mm:ss")} {timeZone}
              </span>
            )}
          </span>
        );
      },
      renderEditCell: (params) => <TimeEditCell params={params} timeZone={timeZone} tripEvents={tripEvents} setTripEvents={setTripEvents} />,
      preProcessEditCellProps: (params) => {
        // Same hazard as the Date cell above: a committed "HH:mm:ss" display string
        // would parse server-side as today's date, moving the event by weeks.
        const existing = tripEvents.find((event) => event.id === params.id)?.time;
        const resolved = resolveTimeCellValue(params.props.value, existing, timeZone);
        if (resolved !== null) {
          const updatedEvents = tripEvents.map((event) =>
            event.id === params.id ? { ...event, time: resolved } : event
          );
          setTripEvents(updatedEvents);
        }
        return { ...params.props };
      },
    },
    {
      field: "routeLocation",
      headerName: "Route Location",
      flex: 1,
      minWidth: 120,
      editable: !readOnly,
      renderCell: (params) => {
        // Only show a name when the stored routeLocation id resolves to an option the closer can
        // actually select. A GPS suggestion can arrive with a pointOfServiceName but no id (no
        // geo.ClientPointToHub row for the trip's hub), and falling back to that name painted a
        // location that was never set: the grid read correct, the edit dropdown opened blank, and
        // validation failed on the missing id. The "Suggested Location" column still shows it.
        const pos = pointsOfService.find((l) => l.id == params.value);
        return <span>{pos?.name || ""}</span>;
      },
      renderEditCell: (params) => (
        <SelectEditCell
          params={params}
          options={pointsOfService.map((l) => ({ value: l.id.toString(), label: l.name }))}
          field="routeLocation"
        />
      ),
      preProcessEditCellProps: (params) => {
        setTripEvents(setRouteLocation(tripEvents, params.id, params.props.value, params.row?.typeId));
        return { ...params.props };
      },
    },
    {
      field: "recommendedPointOfServiceId",
      headerName: "Suggested Location",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        // Build POI display string if available
        const poiDisplay = params.row.poiName
          ? (params.row.poiCategory ? `${params.row.poiName} (${params.row.poiCategory})` : params.row.poiName)
          : null;

        // First try to find client point of service by ID
        const clientPointName = pointsOfService.find((l) => l.id == params.value)?.name;
        if (clientPointName) {
          // Append POI if available
          if (poiDisplay) {
            return (
              <CloserTooltip title="Nearby point of interest from Mapbox" placement="top">
                <span>{clientPointName} - <span style={{ fontStyle: 'italic', color: readableMuted(boldText, '#666') }}>{poiDisplay}</span></span>
              </CloserTooltip>
            );
          }
          return clientPointName;
        }

        // For suggested events, check the pointOfServiceName field (comes directly from GeoApi)
        if (params.row.isSuggested && params.row.pointOfServiceName) {
          // Append POI if available
          if (poiDisplay) {
            return (
              <CloserTooltip title="Nearby point of interest from Mapbox" placement="top">
                <span>{params.row.pointOfServiceName} - <span style={{ fontStyle: 'italic', color: readableMuted(boldText, '#666') }}>{poiDisplay}</span></span>
              </CloserTooltip>
            );
          }
          return params.row.pointOfServiceName;
        }

        // Fall back to Mapbox POI info only
        if (poiDisplay) {
          return (
            <CloserTooltip title="Nearby point of interest from Mapbox" placement="top">
              <span style={{ fontStyle: 'italic', color: readableMuted(boldText, '#666') }}>{poiDisplay}</span>
            </CloserTooltip>
          );
        }

        return null;
      },
    },
    {
      field: "odometer",
      headerName: "Odometer",
      flex: 0.8,
      minWidth: 80,
      editable: !readOnly,
      type: "number",
      valueFormatter: (params) => (params.value ? Math.trunc(params.value).toLocaleString() : ""),
      renderEditCell: (params) => <OdometerEditCell params={params} />,
      preProcessEditCellProps: (params) => {
        // Truncate decimal values to match Dashboard behavior (VB Fix() function)
        const truncatedValue = params.props.value ? Math.trunc(params.props.value) : params.props.value;

        // Auto-populate paired wait event odometer
        const changedTypeId = Number(params.row?.typeId) || tripEvents.find((e) => e.id === params.id)?.typeId;
        const changedTypeName = eventTypes.find((et) => et.id === changedTypeId)?.name;
        const pairedTypeName = waitReasonPairMap[changedTypeName || ""];
        const pairedEvent = pairedTypeName ? findPairedEvent(params.id, pairedTypeName, changedTypeName || "") : null;

        const updatedEvents = tripEvents.map((event) => {
          if (event.id === params.id) return { ...event, odometer: truncatedValue };
          if (pairedEvent && event.id === pairedEvent.id && event.comments?.toLowerCase() !== "assist") return { ...event, odometer: truncatedValue };
          return event;
        });
        setTripEvents(updatedEvents);
        return { ...params.props, value: truncatedValue };
      },
    },
    {
      field: "closingComments",
      headerName: "Closing Comments",
      flex: 2,
      minWidth: 150,
      editable: !readOnly,
      renderCell: (params) => (
        <div style={{ whiteSpace: "normal", wordWrap: "break-word", lineHeight: "1.2", padding: "8px 0" }}>
          {params.value}
        </div>
      ),
      preProcessEditCellProps: (params) => {
        const updatedEvents = tripEvents.map((event) =>
          event.id === params.id ? { ...event, closingComments: params.props.value } : event
        );
        setTripEvents(updatedEvents);
        return { ...params.props };
      },
    },
    {
      field: "eventDriveTimeMiles",
      headerName: "Mi/G-Mi",
      renderCell: (params) => {
        const mi = params.row.eventDriveTimeMiles;
        const gmi = params.row.driveTimeMiles;
        const formattedMi = !mi || mi === 0 ? "--" : Math.round(Math.abs(mi)).toLocaleString();
        const formattedGmi = !gmi || gmi === 0 ? "--" : Math.round(Math.abs(gmi)).toLocaleString();
        return <>{formattedMi}/{formattedGmi}</>;
      },
      flex: 1,
      minWidth: 100,
    },
    {
      field: "eventDriveTimeMinutes",
      headerName: "Mins/GMins",
      flex: 1,
      minWidth: 110,
      renderCell: (params) => {
        let mins = params.row.eventDriveTimeMinutes;
        let formattedMins = "";
        if (!mins) mins = 0;
        const minutes = Math.round(mins);
        if (minutes >= 60) {
          const hours = Math.floor(minutes / 60);
          const mins = minutes % 60;
          formattedMins = `${hours}h ${mins}m`;
        } else {
          formattedMins = minutes ? `${minutes}m` : "--";
        }
        let gmins = params.row.driveTimeMinutes;
        let formattedGmins = "";
        if (!gmins) gmins = 0;
        const roundedGMins = Math.round(gmins);
        if (roundedGMins >= 60) {
          const hours = Math.floor(roundedGMins / 60);
          const mins = roundedGMins % 60;
          formattedGmins = `${hours}h ${mins}m`;
        } else {
          formattedGmins = roundedGMins ? `${roundedGMins}m` : "--";
        }
        return (
          <>
            {formattedMins}/{formattedGmins}
          </>
        );
      },
    },
  ];

  const LoadingSkeleton = () => (
    <Box
      sx={{
        height: "fit-content",
        padding: "0px",
        margin: "0px",
        overflow: "hidden",
      }}
    >
      <table>
        <tbody>
          {[...Array(10)].map((_, index) => (
            <tr key={index}>
              {[...Array(7)].map((_, index) => (
                <td key={index}>
                  <Skeleton
                    key={index}
                    variant="rectangular"
                    width="11.8vw"
                    sx={{ my: 2, mx: 1, overflow: "hidden" }}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );

  const [cellModesModel, setCellModesModel] = React.useState<GridCellModesModel>({});
  const apiRef = useGridApiRef();

  const handleCellClick = useCallback((params: GridCellParams, event: React.MouseEvent) => {
    if (!params.isEditable) {
      return;
    }

    // Ignore portal
    if ((event.target as any).nodeType === 1 && !event.currentTarget.contains(event.target as Element)) {
      return;
    }

    setCellModesModel((prevModel) => {
      return {
        // Revert the mode of the other cells from other rows
        ...Object.keys(prevModel).reduce(
          (acc, id) => ({
            ...acc,
            [id]: Object.keys(prevModel[id]).reduce(
              (acc2, field) => ({
                ...acc2,
                [field]: { mode: GridCellModes.View },
              }),
              {}
            ),
          }),
          {}
        ),
        [params.id]: {
          // Revert the mode of other cells in the same row
          ...Object.keys(prevModel[params.id] || {}).reduce(
            (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
            {}
          ),
          [params.field]: { mode: GridCellModes.Edit },
        },
      };
    });
  }, []);

  const handleCellModesModelChange = useCallback((newModel: GridCellModesModel) => {
    setCellModesModel(newModel);
  }, []);

  const handleProcessRowUpdate = (updated, original) => {
    var updatedEvents = tripEvents.map((e, i) => {
      if (e.id === updated.id) {
        e = updated;
      }
      return e;
    });
    setTripEvents(updatedEvents);
    return updated;
  };

  const navigateToNextCell = useCallback((currentRowId: any, currentField: string, shiftKey: boolean) => {
    const editableColumns = columns.filter(col => col.editable);
    const currentColumnIndex = editableColumns.findIndex(col => col.field === currentField);
    const currentRowIndex = tripEvents.findIndex(row => row.id === currentRowId);

    let nextRowIndex = currentRowIndex;
    let nextColumnIndex = currentColumnIndex;

    if (shiftKey) {
      // Shift+Tab: Move backwards
      nextColumnIndex--;
      if (nextColumnIndex < 0) {
        nextRowIndex--;
        if (nextRowIndex < 0) {
          nextRowIndex = 0;
          nextColumnIndex = 0;
        } else {
          nextColumnIndex = editableColumns.length - 1;
        }
      }
    } else {
      // Tab: Move forwards
      nextColumnIndex++;
      if (nextColumnIndex >= editableColumns.length) {
        nextRowIndex++;
        if (nextRowIndex >= tripEvents.length) {
          nextRowIndex = tripEvents.length - 1;
          nextColumnIndex = editableColumns.length - 1;
        } else {
          nextColumnIndex = 0;
        }
      }
    }

    const nextRow = tripEvents[nextRowIndex];
    const nextColumn = editableColumns[nextColumnIndex];

    if (nextRow && nextColumn) {
      // First, set all cells to view mode to ensure clean state
      setCellModesModel((prevModel) => {
        return Object.keys(prevModel).reduce(
          (acc, id) => ({
            ...acc,
            [id]: Object.keys(prevModel[id]).reduce(
              (acc2, field) => ({
                ...acc2,
                [field]: { mode: GridCellModes.View },
              }),
              {}
            ),
          }),
          {}
        );
      });

      // Navigate to next cell after state has updated
      setTimeout(() => {
        setCellModesModel((prevModel) => ({
          ...prevModel,
          [nextRow.id]: {
            ...prevModel[nextRow.id],
            [nextColumn.field]: { mode: GridCellModes.Edit },
          },
        }));
        if (apiRef.current) {
          apiRef.current.setCellFocus(nextRow.id, nextColumn.field);
        }
      }, 50);
    }
  }, [columns, tripEvents, apiRef]);

  const handleCellKeyDown = useCallback((params: GridCellParams, event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      event.stopPropagation();
      navigateToNextCell(params.id, params.field, event.shiftKey);
    }
    // For dropdown fields, Enter should move to next cell instead of next row
    if (event.key === 'Enter' && ['typeId', 'comments', 'routeLocation'].includes(params.field)) {
      event.preventDefault();
      event.stopPropagation();
      navigateToNextCell(params.id, params.field, false);
    }
  }, [navigateToNextCell]);

  // Add a key down handler on the grid container to capture Tab from edit inputs
  const handleGridKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      // Find which cell is currently being edited
      const editingCells = Object.entries(cellModesModel).flatMap(([rowId, fields]) =>
        Object.entries(fields as any)
          .filter(([_, mode]: [string, any]) => mode.mode === GridCellModes.Edit)
          .map(([field, _]) => ({ rowId, field }))
      );

      if (editingCells.length > 0) {
        event.preventDefault();
        event.stopPropagation();
        const { rowId, field } = editingCells[0];
        navigateToNextCell(rowId, field, event.shiftKey);
      }
    }
  }, [cellModesModel, navigateToNextCell]);

  // Phone/tablet card list — read-only review view. Mirrors the grid columns but
  // stacks each event so nothing is clipped or requires horizontal scrolling.
  const renderMobileCards = () => {
    const tz = GetTimeZoneFromAbbreviation(timeZone);
    const fmtMinutes = (mins: any) => {
      if (!mins) return "--";
      const m = Math.round(mins);
      return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
    };
    const fmtMiles = (v: any) => (!v ? "--" : Math.round(Math.abs(v)).toLocaleString());

    if (!displayedEvents || displayedEvents.length === 0) {
      return (
        <Box sx={{ textAlign: "center", py: 4, color: readableMuted(boldText, "text.secondary") }}>No Events</Box>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
        {displayedEvents.map((row: any) => {
          const typeName = eventTypes.find((et) => et.id == row.typeId)?.name || "—";
          // Same rule as the desktop grid's Route Location cell: no id, no name. suggestedName
          // below still surfaces row.pointOfServiceName, but labelled as a suggestion.
          const routeName = pointsOfService.find((l) => l.id == row.routeLocation)?.name || null;
          const suggestedName =
            pointsOfService.find((l) => l.id == row.recommendedPointOfServiceId)?.name ||
            (row.isSuggested ? row.pointOfServiceName : null) ||
            row.poiName ||
            null;
          const gpsActual =
            row.type === "Begin Trip" ? gpsFirstMove : row.type === "End Trip" ? gpsLastMove : null;
          const timeDisplay = row.time
            ? `${moment.utc(row.time).tz(tz).format("MM/DD/YY HH:mm:ss")} ${timeZone}`
            : null;

          return (
            <Paper
              key={row.id}
              variant="outlined"
              sx={{
                p: 1.5,
                borderLeft: "4px solid",
                borderLeftColor: row.isSuggested ? "#ffc107" : "transparent",
                bgcolor: row.isSuggested ? "#fff3cd" : undefined,
                opacity: row.isActive === false ? 0.6 : 1,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, color: readableMuted(boldText, "text.secondary") }}>#{row.index}</Typography>
                  <Typography sx={{ fontWeight: 600, wordBreak: "break-word" }}>{typeName}</Typography>
                  {row.isSuggested && (
                    <Chip label="Suggested" size="small" sx={{ bgcolor: "#ffe69c", height: 20 }} />
                  )}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                  <Typography variant="caption" sx={{ mr: 0.5 }}>
                    Active
                  </Typography>
                  <Checkbox
                    size="small"
                    checked={row.isActive ?? true}
                    disabled={readOnly}
                    onChange={(e) => {
                      const ev = tripEvents.find((x: any) => x.id === row.id);
                      if (ev && onToggleEventActive) {
                        onToggleEventActive(ev, e.target.checked);
                      } else {
                        setTripEvents(
                          tripEvents.map((x: any) =>
                            x.id === row.id ? { ...x, isActive: e.target.checked } : x
                          )
                        );
                      }
                    }}
                    sx={{ p: 0 }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 1.5, rowGap: 1 }}>
                <CardField label="Time" value={timeDisplay} />
                <CardField label="Odometer" value={row.odometer ? Math.trunc(row.odometer).toLocaleString() : null} />
                <CardField label="Route Location" value={routeName} />
                <CardField label="Suggested Location" value={suggestedName} />
                <CardField label="Mi / G-Mi" value={`${fmtMiles(row.eventDriveTimeMiles)} / ${fmtMiles(row.driveTimeMiles)}`} />
                <CardField label="Mins / G-Mins" value={`${fmtMinutes(row.eventDriveTimeMinutes)} / ${fmtMinutes(row.driveTimeMinutes)}`} />
                {(row.comments || typeName?.toLowerCase().includes("wait")) && (
                  <CardField label="Wait/Cancel Reason" value={row.comments} />
                )}
              </Box>

              {gpsActual && (
                <Box sx={{ mt: 1 }}>
                  <CardField
                    label="GPS Actual"
                    value={`${moment.utc(gpsActual).tz(tz).format("HH:mm:ss")} ${timeZone}`}
                  />
                </Box>
              )}

              {row.closingComments && (
                <Box sx={{ mt: 1 }}>
                  <CardField label="Closing Comments" value={row.closingComments} />
                </Box>
              )}

              {row.eventId && mapDataReady && onGoToMap && (
                <MDButton
                  variant="text"
                  color="info"
                  size="small"
                  onClick={() => onGoToMap(row.eventId)}
                  sx={{ mt: 0.5, pl: 0 }}
                >
                  <MapIcon fontSize="small" />&nbsp;Show in map
                </MDButton>
              )}
            </Paper>
          );
        })}
      </Box>
    );
  };

  // Sits on the totals line at the foot of the grid, so the counts read next to the miles.
  const waitSummary = (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
      <Typography variant="body2" sx={{ fontWeight: "medium", mr: 0.5 }}>
        Waits:
      </Typography>
      <Chip
        label={`Existing: ${waitStats.existingCount} (${waitStats.existingMinutes.toFixed(0)} min)`}
        size="small"
        variant="outlined"
      />
      <Chip
        label={`Suggested: ${waitStats.suggestedCount} (${waitStats.suggestedMinutes.toFixed(0)} min)`}
        size="small"
        variant="outlined"
        sx={{ bgcolor: "#fff3cd", borderColor: "#ffecb5" }}
      />
      <Chip
        label={`Accepted: ${acceptedWaitCount} (${acceptedWaitMinutes.toFixed(0)} min)`}
        size="small"
        color="success"
        variant="outlined"
      />
      <Chip
        label={`Dismissed: ${dismissedWaitCount} (${dismissedWaitMinutes.toFixed(0)} min)`}
        size="small"
        color="error"
        variant="outlined"
      />
    </Box>
  );

  return (
    <>
      <GlobalStyles
        styles={(theme) => ({
          '.MuiMenu-paper .MuiMenuItem-root': {
            '&.Mui-selected': {
              backgroundColor: `${theme.palette.info.main} !important`,
              color: '#ffffff !important',
              fontWeight: 600,
            },
            '&:hover, &.Mui-focusVisible': {
              backgroundColor: `${theme.palette.info.main} !important`,
              color: '#ffffff !important',
            },
          },
          '.MuiMenu-paper:has(.MuiMenuItem-root:hover) .MuiMenuItem-root.Mui-selected:not(:hover)': {
            backgroundColor: 'transparent !important',
            color: 'inherit !important',
            fontWeight: 'normal !important',
          },
          '.MuiMenu-paper:has(.MuiMenuItem-root.Mui-focusVisible) .MuiMenuItem-root.Mui-selected:not(.Mui-focusVisible)': {
            backgroundColor: 'transparent !important',
            color: 'inherit !important',
            fontWeight: 'normal !important',
          },
          // Hide spinner arrows on number inputs
          'input[type=number]::-webkit-outer-spin-button, input[type=number]::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0,
          },
          'input[type=number]': {
            '-moz-appearance': 'textfield',
          },
          // Styling for suggested events
          '.suggested-row': {
            backgroundColor: '#fff3cd !important',
            '&:hover': {
              backgroundColor: '#ffe69c !important',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #ffecb5',
            },
          },
          // Styling for drag and drop target
          '.drop-target-top': {
            '& .MuiDataGrid-cell': {
              borderTop: '3px solid #1976d2 !important',
            },
          },
          '.drop-target-bottom': {
            '& .MuiDataGrid-cell': {
              borderBottom: '3px solid #1976d2 !important',
            },
          },
        })}
      />
      {isLoading && !tripEvents.length ? (
        // Nothing on screen yet, so there is nothing to preserve: a skeleton is honest here.
        LoadingSkeleton()
      ) : (
        <BusyOverlay
          busy={Boolean(busyMessage) || (isLoading && tripEvents.length > 0)}
          message={busyMessage ?? "Loading trip events"}
          detail={busyDetail ?? undefined}
        >
          {/* Its own banner, not a line item in the validation list. Susan, 2026-09-25: "We really
              need to see it in the closing screen" -- and she is working through a pay period's
              worth of these, so it has to be the first thing on the screen rather than one bullet
              among ten. It is ALSO injected into validationErrors upstream, which is what disables
              Approve; this is the part she reads. No close button: the trip is not approvable
              while it stands, so dismissing it would only hide the reason. */}
          {b2bError?.b2BErrorMessage && (
            <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                Rejected by the railroad — correct this before approving
              </Typography>
              <Typography sx={{ fontSize: 13, mt: 0.5 }}>{b2bError.b2BErrorMessage}</Typography>
              {b2bError.expectedDriveTimeMinutes != null && (
                <Typography sx={{ fontSize: 12.5, mt: 0.5 }}>
                  Our records: {b2bError.fromPoint} to {b2bError.toPoint} is{" "}
                  {b2bError.expectedDriveTimeMiles} {b2bError.expectedDriveTimeMiles === 1 ? "mile" : "miles"} and{" "}
                  {b2bError.expectedDriveTimeMinutes}{" "}
                  {b2bError.expectedDriveTimeMinutes === 1 ? "minute" : "minutes"}.
                </Typography>
              )}
              {b2bError.b2BMessageName && (
                <Typography sx={{ fontSize: 11.5, mt: 0.5, opacity: 0.8 }}>{b2bError.b2BMessageName}</Typography>
              )}
            </Alert>
          )}
          {validationErrors.length > 0 && showValidationErrors && (
            <Alert
              severity="error"
              icon={<ErrorIcon />}
              onClose={() => setShowValidationErrors(false)}
              sx={{
                mb: 2,
                "& .MuiAlert-message": {
                  width: "100%",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: "bold" }}>
                  Validation Errors ({validationErrors.length})
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setExpandedValidationErrors(!expandedValidationErrors)}
                  sx={{ ml: 1 }}
                >
                  {expandedValidationErrors ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse in={expandedValidationErrors}>
                <Box
                  component="ul"
                  sx={{
                    margin: 0,
                    paddingLeft: 2,
                    "& li": {
                      marginBottom: 0.5,
                      fontSize: "0.875rem",
                      lineHeight: 1.4,
                    },
                  }}
                >
                  {validationErrors.map((error, index) => {
                    if (typeof error === "string") return <li key={index}>{error}</li>;
                    const relatedTripIds: number[] = error.relatedTripIds || [];
                    return (
                      <li key={index}>
                        {error.message || error}
                        {relatedTripIds.length > 0 && (
                          <>
                            {" ("}
                            {relatedTripIds.map((tid, i) => (
                              <span key={tid}>
                                {i > 0 && ", "}
                                <Box
                                  component="a"
                                  href={`/trips/${tid}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    color: "inherit",
                                    fontWeight: 700,
                                    textDecoration: "none !important",
                                    cursor: "pointer",
                                    "&:hover": { textDecoration: "underline !important" },
                                    "&:focus-visible": { textDecoration: "underline !important", outline: "1px dotted currentColor", outlineOffset: "2px" },
                                  }}
                                >
                                  Trip {tid}
                                </Box>
                              </span>
                            ))}
                            {")"}
                          </>
                        )}
                      </li>
                    );
                  })}
                </Box>
              </Collapse>
            </Alert>
          )}

          {validationWarnings.length > 0 && showValidationErrors && (
            <Alert
              severity="warning"
              icon={<WarningIcon />}
              onClose={() => setShowValidationErrors(false)}
              sx={{
                mb: 2,
                "& .MuiAlert-message": {
                  width: "100%",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: "bold" }}>
                  Validation Warnings ({validationWarnings.length})
                </Typography>
              </Box>
              <Box
                component="ul"
                sx={{
                  margin: 0,
                  paddingLeft: 2,
                  "& li": {
                    marginBottom: 0.5,
                    fontSize: "0.875rem",
                    lineHeight: 1.4,
                  },
                }}
              >
                {validationWarnings.map((warning, index) => (
                  <li key={index}>{typeof warning === "string" ? warning : warning.message || warning}</li>
                ))}
              </Box>
            </Alert>
          )}

          {!isClosed && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This trip is still active.{readOnly ? " Editing is restricted to users with approval permissions." : ""}
            </Alert>
          )}

          <MDBox sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, justifyContent: "space-between", alignItems: { xs: "stretch", lg: "center" }, gap: 1, ml: 1, mb: 2 }}>
            <MDBox sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
              <FormControl size="small" sx={{ minWidth: 120, mt: 1 }}>
                <InputLabel id="active-filter-label" shrink>Filter</InputLabel>
                <Select
                  labelId="active-filter-label"
                  id="active-filter-select"
                  value={activeFilter}
                  label="Filter"
                  onChange={(e) => setActiveFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
              {onVerify && !isApproved && !readOnly && (
                <Box sx={{ display: "flex", alignItems: "center", ml: 2 }}>
                  <Checkbox
                    checked={isVerified ?? false}
                    onChange={(e) => onVerify(e.target.checked)}
                    disabled={verifyLoading}
                    size="small"
                  />
                  <Typography variant="body2" sx={{ ml: 0.5 }}>
                    {verifyLoading ? "Saving..." : "Verified"}
                  </Typography>
                </Box>
              )}
              {onSetManagerReview && !isApproved && !readOnly && (
                <Box sx={{ display: "flex", alignItems: "center", ml: 2, mr: 2 }}>
                  <Checkbox
                    checked={isMapped ?? false}
                    onChange={(e) => onSetManagerReview(e.target.checked)}
                    disabled={managerReviewLoading}
                    size="small"
                  />
                  <Typography variant="body2" sx={{ ml: 0.5 }}>
                    {managerReviewLoading ? "Saving..." : "Manager Review"}
                  </Typography>
                </Box>
              )}
            </MDBox>

            <MDBox sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
              {validationLoaded && (
                <>
                  {/* Show Re-open button when trip is approved and user has permission */}
                  {isApproved && hasAllowApprove && onReopen && (
                    <MDButton
                      variant="contained"
                      color="info"
                      sx={{ mr: 2 }}
                      onClick={onReopen}
                      disabled={approveLoading}
                    >
                      {approveLoading ? "Re-opening Trip ..." : "Re-open"}
                    </MDButton>
                  )}

                  {/* Hide approve/override/deactivate buttons when trip is approved or readOnly */}
                  {!isApproved && !readOnly && (
                    <>
                  {/* Show Approve button if user has AllowApprove permission
                      - Enabled if no validation errors
                      - Disabled if validation errors exist
                      - Hidden if user has both AllowApprove and AllowOverride AND there are errors */}
                  {hasAllowApprove && !(hasAllowOverride && validationErrors.length > 0) && (
                    <>
                      {!approveLoading ? (
                        <MDButton
                          variant="contained"
                          color="success"
                          sx={{ mr: 2 }}
                          onClick={onApprove}
                          disabled={validationErrors.length > 0}
                        >
                          Approve
                        </MDButton>
                      ) : (
                        <MDButton variant="contained" color="success" sx={{ mr: 2 }} disabled>
                          {approveStatus ?? "Approving Trip ..."}
                        </MDButton>
                      )}
                    </>
                  )}
                  {/* Show Override & Approve button when:
                      - User has AllowApprove permission AND
                      - User has AllowOverride permission AND
                      - There are validation errors
                      - Enabled if canOverride is true (from overrideValidation.canOverride)
                      - Disabled if canOverride is false (errors not overridable)
                      - Tooltip shows reason from overrideValidation.reason */}
                  {hasAllowApprove && hasAllowOverride && validationErrors.length > 0 && onOverrideFinalize && (
                    <CloserTooltip title={overrideReason || ""} placement="top">
                      <span>
                        {!approveLoading ? (
                          <MDButton
                            variant="contained"
                            color="warning"
                            sx={{ mr: 2 }}
                            onClick={onOverrideFinalize}
                            disabled={!canOverride}
                          >
                            Override & Approve
                          </MDButton>
                        ) : (
                          <MDButton variant="contained" color="warning" sx={{ mr: 2 }} disabled>
                            {approveStatus ?? "Overriding & Approving Trip ..."}
                          </MDButton>
                        )}
                      </span>
                    </CloserTooltip>
                  )}
                  {/* Grid-level add. Deliberately NOT a per-row control: the per-row "+"
                      disappears along with the rows, which is exactly the case that needs
                      an add. Sits with the toolbar buttons so it is reachable with zero
                      rows on screen, and inherits the same !isApproved && !readOnly gate
                      as everything else in this block. */}
                  <CloserTooltip
                    title="Add one blank event at the end of the list — use this to hand-add a missing Begin Trip or End Trip. The + on a row inserts a wait pair above that row instead."
                    placement="top"
                  >
                    <span>
                      <MDButton
                        variant="outlined"
                        color="info"
                        sx={{ mr: 2 }}
                        onClick={addEventAtEnd}
                        disabled={approveLoading || eventResetLoading}
                        startIcon={<AddBox />}
                      >
                        Add Event
                      </MDButton>
                    </span>
                  </CloserTooltip>
                  <CloserTooltip
                    title={
                      applicableSuggestionCount
                        ? "Set each row's Route Location to its Suggested Location, as if picked by hand. Nothing is saved until Save and Validate."
                        : "No row has a suggested location that differs from its Route Location"
                    }
                    placement="top"
                  >
                    <span>
                      <MDButton
                        variant="outlined"
                        color="info"
                        sx={{ mr: 2 }}
                        onClick={handleApplySuggestedLocations}
                        disabled={approveLoading || eventResetLoading || !applicableSuggestionCount}
                      >
                        Apply Suggested Locations
                      </MDButton>
                    </span>
                  </CloserTooltip>
                  {allowApplyGpsOdometers && (
                    <CloserTooltip
                      title={
                        mapDataReady
                          ? "Re-derive every active event's odometer from the vehicle's GPS track. Make your time and order edits first, then apply."
                          : "Waiting for GPS activity to load"
                      }
                      placement="top"
                    >
                      <span>
                        <MDButton
                          variant="contained"
                          color="info"
                          sx={{ mr: 2 }}
                          onClick={handleApplyGpsOdometers}
                          disabled={approveLoading || !tripEvents.length || !mapDataReady}
                          startIcon={<GpsFixedIcon />}
                        >
                          Apply GPS Odometers
                        </MDButton>
                      </span>
                    </CloserTooltip>
                  )}
                  {allowEventReset && (
                    <CloserTooltip
                      title="Delete every event except Begin Trip and End Trip - driver waits included - and rebuild the trip from GPS. Cannot be undone."
                      placement="top"
                    >
                      <span>
                        <MDButton
                          variant="contained"
                          color="error"
                          sx={{ mr: 2 }}
                          onClick={onEventReset}
                          disabled={approveLoading || eventResetLoading}
                          startIcon={<RestartAltIcon />}
                        >
                          {eventResetLoading ? "Resetting..." : "Event Reset"}
                        </MDButton>
                      </span>
                    </CloserTooltip>
                  )}
                  <MDButton variant="contained" color="primary" sx={{ mr: 2 }} onClick={onSaveAndValidate} disabled={approveLoading}>
                    Save and Validate
                  </MDButton>
                  {/* Show Deactivate All button only if user has AllowClearAllEvents permission */}
                  {hasAllowClearAllEvents && onDeactivateAll && (
                    <MDButton variant="outlined" color="error" sx={{ mr: 2 }} onClick={onDeactivateAll} disabled={approveLoading}>
                      Deactivate All
                    </MDButton>
                  )}
                    </>
                  )}
                </>
              )}
            </MDBox>
          </MDBox>
          
          {/* An Event Reset used to skeleton this region on its own, which meant a reset showed
              two indicators at once: this one and the overlay covering the whole grid. One is
              enough, and the overlay is the one that can say which phase the reset is in. */}
          {isMobile ? (
            <>
              {!gridCollapsed && renderMobileCards()}
              <MDBox sx={{ px: 1, py: 1 }}>{waitSummary}</MDBox>
            </>
          ) : (
          <Paper
            sx={{ width: "99%", height: "auto", overflow: "hidden" }}
            onKeyDown={handleGridKeyDown}
          >
            <DataGrid
              apiRef={apiRef}
              autoHeight={!gridCollapsed}
              editMode="cell"
              sx={{
                overflow: "hidden",
                fontSize: "1em",
                ...readableText(boldText),
                "& .MuiDataGrid-cell": readableText(boldText, { fontWeight: 400, color: "rgba(0, 0, 0, 0.87)" }),
                "& .MuiDataGrid-columnHeaders": {
                  color: boldText ? "#000" : "rgba(0, 0, 0, 0.87)",
                  display: gridCollapsed ? "none" : "flex",
                },
                "& .MuiDataGrid-virtualScroller": {
                  display: gridCollapsed ? "none" : "block",
                },
                "& .MuiDataGrid-footerContainer": {
                  display: gridCollapsed ? "none" : "flex",
                },
                "& .MuiDataGrid-overlay": {
                  display: gridCollapsed ? "none" : "flex",
                },
              }}
              rows={displayedEvents || []}
              columns={columns}
              disableColumnMenu
              disableRowSelectionOnClick
              density="compact"
              getRowHeight={() => "auto"}
              slots={{
                toolbar: () => (
                  <GridToolbarContainer sx={{ justifyContent: "flex-end", p: 1, gap: 1 }}>
                    {onExport && (
                      <CloserTooltip title="Export Events to Excel">
                        <IconButton
                          color="primary"
                          onClick={onExport}
                          size="small"
                          disabled={exportLoading}
                        >
                          {exportLoading ? <CircularProgress size={20} /> : <ExportIcon />}
                        </IconButton>
                      </CloserTooltip>
                    )}
                    <CloserTooltip title={gridCollapsed ? "Show Events Grid" : "Hide Events Grid"}>
                      <IconButton
                        color="info"
                        onClick={() => setGridCollapsed(!gridCollapsed)}
                        size="small"
                      >
                        {gridCollapsed ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </CloserTooltip>
                  </GridToolbarContainer>
                ),
                noRowsOverlay: () => (
                  <Stack height="100%" alignItems="center" justifyContent="center">
                    No Events
                  </Stack>
                ),
              }}
              slotProps={{
                row: {
                  onDragOver: (e: React.DragEvent<HTMLDivElement>) => {
                    // Only process if we're dragging a row
                    if (!draggedRow) return;
                    const rowId = e.currentTarget.getAttribute('data-id');
                    const row = tripEvents?.find(r => r.id.toString() === rowId);
                    if (row) handleDragOver(e, row);
                  },
                  onDrop: (e: React.DragEvent<HTMLDivElement>) => {
                    // Only process if we're dragging a row
                    if (!draggedRow) return;
                    const rowId = e.currentTarget.getAttribute('data-id');
                    const row = tripEvents?.find(r => r.id.toString() === rowId);
                    if (row) handleDrop(e, row);
                  },
                },
              }}
              loading={false}
              getRowClassName={(params) => {
                const classes = [];

                if (params.row.isSuggested) classes.push("suggested-row");
                else classes.push(params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd");

                if (dropTargetRow?.id === params.row.id) {
                  classes.push(dropPosition === 'top' ? 'drop-target-top' : 'drop-target-bottom');
                }

                return classes.join(' ');
              }}
              cellModesModel={cellModesModel}
              onCellModesModelChange={handleCellModesModelChange}
              onCellClick={handleCellClick}
              onCellKeyDown={handleCellKeyDown}
              processRowUpdate={handleProcessRowUpdate}
            />
            <MDBox
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 1,
                fontSize: "1em",
                ...readableText(boldText, { color: "rgba(0, 0, 0, 0.87)" }),
              }}
            >
              {waitSummary}
              {!gridCollapsed && (
                <span>
                  Total Mi/G-Mi: {Math.round(milesTotals.miles).toLocaleString()}/{Math.round(milesTotals.gpsMiles).toLocaleString()}
                </span>
              )}
            </MDBox>
          </Paper>
          )}
        </BusyOverlay>
      )}
    </>
  );
};

export default CloserEventsGrid;